import PropTypes from 'prop-types';
import { FlexTable } from '@evertrue/sdk';
import { createInteractionTableColumns } from './interaction-table-columns';
import { useInteractionSorting } from '../hooks/use-interaction-sorting';
import { useState, useCallback } from 'react';

/**
 * InteractionTable - Displays paginated table of interactions/notes
 *
 * Renders interaction records (meetings, calls, emails) with server-side pagination
 * and sorting. Used throughout the app for contact interactions and search results.
 *
 * Data flow: notes prop → custom hooks → FlexTable → user callbacks
 */
const InteractionTable = ({
  notes,
  loading,
  highlight,
  showContact,
  showFullText,
  contactId,
  showMessage,
  // Pagination props
  total = 0,
  page = 1,
  limit,
  onPageChange = () => {},
  onPageSizeChange = () => {},
  sortProp = '',
  sortReverse = false,
  onSort = () => {},
  showToolbar = true,
  showFooter = true,
}) => {
  // Handle pagination state directly in component (like proposals table)

  const [paginationModel, setPaginationModel] = useState({
    page: page - 1 || 0,
    pageSize: limit,
  });

  const handlePaginationModelChange = useCallback(
    (model) => {
      const prevPage = paginationModel.page;
      const prevPageSize = paginationModel.pageSize;

      setPaginationModel(model);

      // Only call onPageChange if the page actually changed
      if (model.page !== prevPage) {
        onPageChange(model.page + 1);
      }

      // Only call onPageSizeChange if the page size actually changed
      if (model.pageSize !== prevPageSize) {
        onPageSizeChange(model.pageSize);
      }
    },
    [onPageChange, onPageSizeChange, paginationModel.page, paginationModel.pageSize]
  );

  // Page size validation is handled in the store layer

  // Extract sorting logic into custom hook for reusability
  const { sortModel, handleSortModelChange } = useInteractionSorting({
    sortProp,
    sortReverse,
    onSort,
  });

  // Create columns using the extracted function
  const columns = createInteractionTableColumns({
    highlight,
    showContact,
    showFullText,
    showMessage,
    contactId,
  });

  return (
    <FlexTable
      tableKey="interaction-feed-table"
      columns={columns}
      rows={notes || []}
      emptyStateMessage="No interactions were found matching your search criteria."
      emptyStateMessageSubtext="Try adjusting your filters to find results."
      rowCount={total}
      paginationModel={paginationModel}
      onPaginationModelChange={handlePaginationModelChange}
      pageSizeOptions={[10, 25, 50, 100]}
      sortModel={sortModel}
      onSortModelChange={handleSortModelChange}
      getRowHeight={() => 'auto'}
      autosizeOptions={{
        columns: ['interaction'],
        includeOutliers: true,
        includeHeaders: true,
        expand: true,
      }}
      loading={loading}
      showToolbar={showToolbar}
      hideFooter={!showFooter}
      getRowClassName={(params) => (params.row?.pinned ? 'interaction-list-item--pinned' : '')}
      {...(!showToolbar && {
        slots: {
          columnHeaders: () => null, // Renders nothing for the column headers
        },
      })}
    />
  );
};

InteractionTable.propTypes = {
  notes: PropTypes.array,
  loading: PropTypes.bool,
  highlight: PropTypes.string,
  showContact: PropTypes.bool,
  showFullText: PropTypes.bool,
  showMessage: PropTypes.bool,
  contactId: PropTypes.number,
  // Pagination props
  total: PropTypes.number,
  page: PropTypes.number,
  limit: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  sortProp: PropTypes.string,
  sortReverse: PropTypes.bool,
  onSort: PropTypes.func,
  showToolbar: PropTypes.bool,
  showFooter: PropTypes.bool,
};

export default InteractionTable;
